import React, { useState, useEffect, useCallback } from 'react';
import { Printer, RefreshCw, CheckCircle, XCircle, Wifi, AlertTriangle } from 'lucide-react';
import api from '@/utils/apiClient';
import { toast } from 'sonner';

/**
 * Print Server Page
 * 
 * Run this on a Mac/PC connected to the receipt printer.
 * It polls the server for print jobs and prints them automatically.
 * 
 * Setup:
 * 1. Connect Star printer to this computer via USB
 * 2. Set Star printer as default printer
 * 3. Open this page in Chrome
 * 4. Click "Start Print Server"
 * 5. Orders from the POS will automatically print here
 */
export default function PrintServerPage() {
  const [isRunning, setIsRunning] = useState(false);
  const [lastPoll, setLastPoll] = useState(null);
  const [printedJobs, setPrintedJobs] = useState([]);
  const [error, setError] = useState(null);
  const [station, setStation] = useState('customer'); // Which station this print server handles
  const [stuckJobCount, setStuckJobCount] = useState(0);
  const [recoveringJobs, setRecoveringJobs] = useState(false);

  // Check for stuck jobs periodically
  const checkStuckJobs = useCallback(async () => {
    try {
      const res = await api.get(`/api/print-server/status/${station}`);
      setStuckJobCount(res.data.stuck || 0);
    } catch (err) {
      console.error('Failed to check stuck jobs:', err);
    }
  }, [station]);

  // Recover stuck jobs
  const recoverStuckJobs = async () => {
    setRecoveringJobs(true);
    try {
      const res = await api.post(`/api/print-server/recover-stuck-jobs/${station}`);
      if (res.data.recovered_count > 0) {
        toast.success(`Recovered ${res.data.recovered_count} stuck print jobs`);
        setStuckJobCount(0);
      } else {
        toast.info('No stuck jobs to recover');
      }
    } catch (err) {
      toast.error('Failed to recover stuck jobs: ' + (err.message || 'Unknown error'));
    } finally {
      setRecoveringJobs(false);
    }
  };

  // Check for stuck jobs every minute when running
  useEffect(() => {
    if (isRunning) {
      checkStuckJobs();
      const interval = setInterval(checkStuckJobs, 60000);
      return () => clearInterval(interval);
    }
  }, [isRunning, checkStuckJobs]);

  const pollForJobs = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get(`/api/print-server/poll/${station}`);
      const data = res.data;
      
      setLastPoll(new Date().toLocaleTimeString());
      
      if (data.job) {
        // We have a job to print!
        console.log('Print job received:', data.job);
        printJob(data.job);
        
        // Mark job as printed
        await api.post(`/api/print-server/complete/${data.job.job_id}`);
        
        setPrintedJobs(prev => [{
          id: data.job.job_id,
          orderNumber: data.job.order_number,
          time: new Date().toLocaleTimeString(),
          station: data.job.station
        }, ...prev.slice(0, 19)]); // Keep last 20
      }
    } catch (err) {
      console.error('Poll error:', err);
      setError(err.message || 'Failed to poll for jobs');
    }
  }, [station]);

  // Polling interval
  useEffect(() => {
    let interval;
    if (isRunning) {
      pollForJobs(); // Poll immediately
      interval = setInterval(pollForJobs, 3000); // Then every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, pollForJobs]);

  const printJob = (job) => {
    // Check if this is a drawer-only job (just open cash drawer, no receipt)
    // This is used when POS skips customer receipt but still needs to open drawer for cash payments
    if (job.is_drawer_only || job.print_data?.drawer_only) {
      openCashDrawer();
      return;
    }
    
    // Check if this is a cash drawer kick command (legacy support)
    if (job.print_data?.job_type === 'drawer_kick' || job.is_drawer_kick) {
      openCashDrawer();
      return;
    }
    
    const printWindow = window.open('', '_blank', 'width=300,height=600');
    
    if (!printWindow) {
      toast.error('Please allow popups for printing');
      return;
    }
    
    const receiptHTML = generateReceiptHTML(job);
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      }, 250);
    };
    
    toast.success(`Printing order #${job.order_number}`);
  };

  const openCashDrawer = () => {
    // Send drawer kick command via a minimal print
    // The Star printer's cash drawer opens when ANY print job is sent
    // We use an invisible/empty print to trigger only the drawer
    const drawerWindow = window.open('', '_blank', 'width=1,height=1');
    if (!drawerWindow) {
      toast.error('Please allow popups to open cash drawer');
      return;
    }
    
    // Absolutely minimal print - just whitespace, no visible content
    // This triggers the printer's cash drawer kick without printing anything visible
    drawerWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<style>
@page { size: 80mm 1mm; margin: 0; }
@media print { body { visibility: hidden; } }
body { margin: 0; padding: 0; height: 0; }
</style>
</head>
<body></body>
</html>`);
    drawerWindow.document.close();
    
    drawerWindow.onload = function() {
      setTimeout(() => {
        drawerWindow.focus();
        drawerWindow.print();
        setTimeout(() => {
          drawerWindow.close();
        }, 300);
      }, 100);
    };
    
    toast.success('Cash drawer opened!');
  };

  const generateReceiptHTML = (job) => {
    const { print_data } = job;
    const isStationTicket = job.station !== 'customer';
    
    if (isStationTicket) {
      return generateStationTicketHTML(job);
    }
    
    // Customer receipt
    const items = print_data?.items || [];
    let itemsHTML = '';
    items.forEach(item => {
      itemsHTML += `
      <div class="row">
        <span class="item-name">${item.quantity || 1}x ${item.name}</span>
        <span class="item-price">$${(item.total_price || item.price || 0).toFixed(2)}</span>
      </div>`;
      if (item.size) {
        itemsHTML += `<div class="modifier">   Size: ${item.size}</div>`;
      }
      if (item.customizations?.length) {
        item.customizations.forEach(c => {
          itemsHTML += `<div class="modifier">   - ${c}</div>`;
        });
      }
    });

    return `<!DOCTYPE html>
<html>
<head>
  <title>Receipt #${job.order_number}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; padding: 5mm; line-height: 1.4; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .large { font-size: 16px; }
    .divider { border-top: 1px dashed #000; margin: 8px 0; }
    .double-divider { border-top: 2px solid #000; margin: 8px 0; }
    .row { display: flex; justify-content: space-between; }
    .item-name { flex: 1; }
    .item-price { text-align: right; min-width: 60px; }
    .modifier { font-size: 11px; color: #333; }
  </style>
</head>
<body>
  <div class="center bold large">BULACCINO CAFE</div>
  <div class="center">SUITE 8, JAGDISH GOSAI COMPLEX</div>
  <div class="center">Namaka, Nadi, Fiji</div>
  <div class="center">TIN: 2901609577</div>
  <br>
  <div class="double-divider"></div>
  <div class="bold">Order #: ${job.order_number}</div>
  <div>Date: ${print_data?.date || new Date().toLocaleDateString()}</div>
  <div>Time: ${print_data?.time || new Date().toLocaleTimeString()}</div>
  <div>Customer: ${print_data?.customer_name || 'Guest'}</div>
  <br>
  <div class="divider"></div>
  ${itemsHTML}
  <div class="divider"></div>
  <div class="row"><span>Subtotal:</span><span>$${(print_data?.subtotal || 0).toFixed(2)}</span></div>
  <div class="row"><span>VAT:</span><span>$${(print_data?.vat_amount || 0).toFixed(2)}</span></div>
  ${(print_data?.discount > 0 || print_data?.discount_amount > 0) ? `<div class="row"><span>${
    print_data?.discount_label || 
    (print_data?.discount_percent > 0 ? `Discount (${print_data.discount_percent}%)` : 'Discount')
  }:</span><span>-$${(print_data.discount || print_data.discount_amount || 0).toFixed(2)}</span></div>` : ''}
  ${print_data?.eftpos_surcharge > 0 ? `<div class="row"><span>EFTPOS Surcharge (4%):</span><span>+$${(print_data.eftpos_surcharge || 0).toFixed(2)}</span></div>` : ''}
  <div class="double-divider"></div>
  <div class="row bold large"><span>TOTAL:</span><span>$${(print_data?.total || 0).toFixed(2)}</span></div>
  <br>
  ${print_data?.split_payments && print_data.split_payments.length > 0 ? `
  <div class="divider"></div>
  <div class="bold center">SPLIT PAYMENT</div>
  ${print_data.split_payments.map((sp, idx) => `
    <div class="row"><span>Payment ${idx + 1} (${sp.methodLabel || sp.method}):</span><span>$${(sp.amount || 0).toFixed(2)}</span></div>
    ${sp.eftpos_surcharge > 0 ? `<div class="row" style="font-size:10px;"><span>&nbsp;&nbsp;Includes 4% surcharge:</span><span>+$${(sp.eftpos_surcharge || 0).toFixed(2)}</span></div>` : ''}
  `).join('')}
  <div class="divider"></div>
  ` : `
  <div>Payment: ${(print_data?.payment_method || 'CASH').toUpperCase()}${print_data?.eftpos_card_type === 'overseas' ? ' (Overseas)' : ''}</div>
  `}
  ${print_data?.payment_method === 'cash' && print_data?.cash_received > 0 ? `
  <div class="row"><span>Cash Received:</span><span>$${(print_data.cash_received || 0).toFixed(2)}</span></div>
  <div class="row"><span>Change:</span><span>$${(print_data.change_given || 0).toFixed(2)}</span></div>
  ` : ''}
  <br>
  <div class="divider"></div>
  <br>
  <div class="center">Vinaka - Thank You!</div>
  <div class="center">bulaccinocafe.com</div>
  <br><br><br>
</body>
</html>`;
  };

  const generateStationTicketHTML = (job) => {
    const { print_data, station } = job;
    const stationUpper = station.toUpperCase();
    const items = print_data?.items || [];
    
    let itemsHTML = '';
    items.forEach(item => {
      itemsHTML += `<div class="item bold">${item.quantity || 1}x ${item.name}</div>`;
      if (item.size) itemsHTML += `<div class="modifier">   Size: ${item.size}</div>`;
      if (item.customizations?.length) {
        item.customizations.forEach(c => {
          itemsHTML += `<div class="modifier">   - ${c}</div>`;
        });
      }
    });

    return `<!DOCTYPE html>
<html>
<head>
  <title>${stationUpper} #${job.order_number}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Courier New', monospace; font-size: 14px; width: 80mm; padding: 5mm; line-height: 1.5; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .large { font-size: 18px; }
    .xlarge { font-size: 24px; }
    .divider { border-top: 2px dashed #000; margin: 10px 0; }
    .item { font-size: 16px; margin: 8px 0; }
    .modifier { font-size: 13px; }
  </style>
</head>
<body>
  <div class="center xlarge bold">** ${stationUpper} **</div>
  <br>
  <div class="divider"></div>
  <div class="center large bold">Order #${job.order_number}</div>
  <div class="center">Time: ${print_data?.time || new Date().toLocaleTimeString()}</div>
  <div class="center">Customer: ${print_data?.customer_name || 'Guest'}</div>
  <div class="divider"></div>
  <br>
  ${itemsHTML}
  <br>
  <div class="divider"></div>
  <br><br><br>
</body>
</html>`;
  };

  const handleTestPrint = () => {
    const testJob = {
      job_id: 'test-' + Date.now(),
      order_number: 'TEST-001',
      station: station,
      print_data: {
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        customer_name: 'Test Customer',
        items: [
          { name: 'Test Coffee', quantity: 1, price: 5.00, total_price: 5.00 },
          { name: 'Test Muffin', quantity: 2, price: 3.50, total_price: 7.00 }
        ],
        subtotal: 12.00,
        vat_amount: 1.80,
        total: 13.80,
        payment_method: 'cash'
      }
    };
    printJob(testJob);
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Printer className="w-8 h-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Print Server</h1>
              <p className="text-gray-500 dark:text-gray-400">Receives print jobs from POS</p>
            </div>
          </div>

          {/* Station Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Station
            </label>
            <select
              value={station}
              onChange={(e) => setStation(e.target.value)}
              className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
              disabled={isRunning}
            >
              <option value="customer">Customer Receipt (POS)</option>
              <option value="barista">Barista Station</option>
              <option value="kitchen">Kitchen Station</option>
              <option value="juicer">Juicer Station</option>
              <option value="counter">Counter (Display Case)</option>
              <option value="kiosk">Kiosk Receipt</option>
            </select>
          </div>

          {/* Status */}
          <div className={`p-4 rounded-lg mb-6 ${isRunning ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
            <div className="flex items-center gap-2">
              {isRunning ? (
                <>
                  <Wifi className="w-5 h-5 text-green-600 animate-pulse" />
                  <span className="font-medium text-green-700 dark:text-green-400">
                    Print Server Running - Listening for {station} jobs
                  </span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-gray-400" />
                  <span className="font-medium text-gray-600 dark:text-gray-400">
                    Print Server Stopped
                  </span>
                </>
              )}
            </div>
            {lastPoll && (
              <p className="text-sm text-gray-500 mt-1">Last poll: {lastPoll}</p>
            )}
            {error && (
              <p className="text-sm text-red-500 mt-1">{error}</p>
            )}
          </div>

          {/* Stuck Jobs Warning */}
          {stuckJobCount > 0 && (
            <div className="p-4 rounded-lg mb-6 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  <span className="font-medium text-yellow-700 dark:text-yellow-400">
                    {stuckJobCount} stuck print job{stuckJobCount > 1 ? 's' : ''} detected
                  </span>
                </div>
                <button
                  onClick={recoverStuckJobs}
                  disabled={recoveringJobs}
                  className="px-3 py-1 text-sm font-medium bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg flex items-center gap-1 disabled:opacity-50"
                >
                  {recoveringJobs ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Recovering...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Recover
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                These jobs have been stuck in &quot;printing&quot; status for more than 5 minutes.
              </p>
            </div>
          )}

          {/* Controls */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setIsRunning(!isRunning)}
              className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
                isRunning 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-teal-600 hover:bg-teal-700 text-white'
              }`}
            >
              {isRunning ? (
                <>
                  <XCircle className="w-5 h-5" />
                  Stop Print Server
                </>
              ) : (
                <>
                  <Printer className="w-5 h-5" />
                  Start Print Server
                </>
              )}
            </button>
            
            <button
              onClick={handleTestPrint}
              className="py-3 px-4 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 flex items-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Test Print
            </button>
            
            <button
              onClick={openCashDrawer}
              className="py-3 px-4 rounded-lg font-medium bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-2"
            >
              💰 Open Drawer
            </button>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Setup Instructions:</h3>
            <ol className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
              <li>Connect Star printer to this Mac/PC via USB</li>
              <li>Set Star printer as default printer</li>
              <li>Select the station this printer handles</li>
              <li>Click &quot;Start Print Server&quot;</li>
              <li>Keep this page open - orders will print automatically</li>
            </ol>
          </div>

          {/* Recent Jobs */}
          {printedJobs.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Print Jobs:</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {printedJobs.map((job, i) => (
                  <div key={job.id + i} className="flex items-center gap-2 text-sm p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Order #{job.orderNumber}</span>
                    <span className="text-gray-400">({job.station})</span>
                    <span className="ml-auto text-gray-400">{job.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
